/* eslint-disable */
import React, { useState, useRef, useEffect } from 'react';
import { IC, INPUT_CLS } from './ebookShared';
import adminServices from '../../services/adminServices';

function Ico({ d, size, cls }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size||16} height={size||16}
      viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={cls||''}>
      <path d={d} />
    </svg>
  );
}

// ─── AI Drawer ────────────────────────────────────────────────────────────────
// - Creates/reuses a chat session per topic
// - Saves every message to ebook_ai_messages
// - Backend handles 30-message-per-topic cleanup on each insert
// - Passes message history as context on each API call
export function AIDrawer({ onClose, selectedText, activeTopic }) {
  const [input, setInput]       = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [initLoading, setInitLoading] = useState(true);
  const endRef = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Load or create session + fetch message history when topic changes
  useEffect(() => {
    if (!activeTopic?.topicId) {
      setMessages([{ from: 'ai', text: 'Hi! Ask me anything about this topic.' }]);
      setInitLoading(false);
      return;
    }

    setInitLoading(true);
    setMessages([]);
    setSessionId(null);

    adminServices.getOrCreateAiChatSession({ topicId: activeTopic.topicId })
      .then((res) => {
        const sid = res?.data?.sessionId;
        setSessionId(sid);
        return adminServices.getAiChatMessages(sid);
      })
      .then((res) => {
        const history = Array.isArray(res?.data) ? res.data : [];
        if (history.length === 0) {
          const greeting = selectedText
            ? `I see you selected: "${selectedText.slice(0, 80)}${selectedText.length > 80 ? '…' : ''}" — what would you like to know?`
            : `Hi! Ask me anything about "${activeTopic.topicTitle}".`;
          setMessages([{ from: 'ai', text: greeting, isLocal: true }]);
        } else {
          setMessages(history.map((m) => ({ from: m.role === 'user' ? 'user' : 'ai', text: m.content })));
        }
      })
      .catch(() => {
        setMessages([{ from: 'ai', text: `Hi! Ask me anything about "${activeTopic?.topicTitle || 'this topic'}".`, isLocal: true }]);
      })
      .finally(() => setInitLoading(false));
  }, [activeTopic?.topicId]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');

    // Optimistically add user message
    const userMsg = { from: 'user', text: msg };
    setMessages((p) => [...p, userMsg]);
    setLoading(true);

    try {
      // Build context from current messages (exclude local-only greeting)
      const historyForAPI = messages
        .filter((m) => !m.isLocal)
        .map((m) => ({ role: m.from === 'user' ? 'user' : 'assistant', content: m.text }));

      // Save user message + get AI response from backend
      // Backend handles: save to DB, trim to 30 messages, call AI with context
      const res = await adminServices.sendAiChatMessage({
        sessionId,
        topicId:      activeTopic?.topicId,
        role:         'user',
        content:      msg,
        selectedText: selectedText || null,
        history:      historyForAPI,
      });

      const aiText = res?.data?.reply || 'Sorry, I could not generate a response. Please try again.';
      setMessages((p) => [...p, { from: 'ai', text: aiText }]);
    } catch {
      setMessages((p) => [...p, { from: 'ai', text: 'Something went wrong. Please check your connection and try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const QUICK = ['Explain simpler', 'Give an example', 'Summarise topic', 'Create flashcards'];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-white flex-shrink-0">
        <div className="w-8 h-8 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <Ico d={IC.sparkle} size={15} cls="text-white" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-slate-900">AI Learning Assistant</p>
          {activeTopic && <p className="text-xs text-slate-400 truncate">{activeTopic.topicTitle}</p>}
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1">
          <Ico d={IC.x} size={16} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
        {initLoading ? (
          <div className="flex flex-col gap-3 py-4">
            {[1,2].map((i) => (
              <div key={i} className="flex gap-2">
                <div className="w-7 h-7 bg-slate-100 rounded-full flex-shrink-0 animate-pulse" />
                <div className="flex-1 h-12 bg-slate-100 rounded-2xl animate-pulse" />
              </div>
            ))}
          </div>
        ) : messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.from === 'user' ? 'flex-row-reverse' : ''}`}>
            {m.from === 'ai' && (
              <div className="w-7 h-7 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Ico d={IC.sparkle} size={12} cls="text-white" />
              </div>
            )}
            <div className={`max-w-[85%] text-xs leading-relaxed rounded-2xl px-3.5 py-2.5 whitespace-pre-line ${m.from === 'ai' ? 'bg-slate-100 text-slate-800 rounded-tl-sm' : 'bg-indigo-600 text-white rounded-tr-sm'}`}>
              {m.text}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex gap-2">
            <div className="w-7 h-7 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
              <Ico d={IC.sparkle} size={12} cls="text-white" />
            </div>
            <div className="bg-slate-100 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1 items-center">
              {[0,1,2].map((i) => (
                <span key={i} className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: `${i*0.15}s` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Quick actions */}
      <div className="px-3 pb-2 flex gap-1.5 flex-wrap flex-shrink-0">
        {QUICK.map((q) => (
          <button key={q} onClick={() => send(q)}
            disabled={loading}
            className="text-xs px-2.5 py-1 rounded-full border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 whitespace-nowrap disabled:opacity-50">
            {q}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="flex gap-2 p-3 border-t border-slate-200 flex-shrink-0">
        <input
          className={`${INPUT_CLS} flex-1`}
          placeholder="Ask anything…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          disabled={loading}
        />
        <button onClick={() => send()}
          disabled={loading || !input.trim()}
          className="bg-indigo-600 text-white w-9 h-9 rounded-xl flex items-center justify-center hover:bg-indigo-700 flex-shrink-0 disabled:opacity-50">
          <Ico d={IC.send} size={14} />
        </button>
      </div>
    </div>
  );
}

// ─── Highlights Panel ─────────────────────────────────────────────────────────
export function HighlightsPanel({ onClose, highlights = [], onDelete }) {
  const COLORS = ['#FCD34D','#F472B6','#86EFAC','#93C5FD','#D8B4FE','#FDBA74'];
  const [colorFilter, setColorFilter] = useState(null);
  const filtered = colorFilter ? highlights.filter((h) => h.colorHex === colorFilter) : highlights;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 flex-shrink-0">
        <Ico d={IC.highlight} size={16} cls="text-indigo-500" />
        <p className="text-sm font-bold text-slate-900 flex-1">Highlights</p>
        <span className="text-xs font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{highlights.length}</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 ml-1"><Ico d={IC.x} size={16} /></button>
      </div>
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-slate-100 flex-shrink-0 overflow-x-auto">
        <button onClick={() => setColorFilter(null)} className={`px-2.5 py-1 rounded-full text-xs font-bold ${!colorFilter ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}`}>All</button>
        {COLORS.map((c) => (
          <button key={c} onClick={() => setColorFilter(colorFilter === c ? null : c)}
            style={{ background: c }} className={`w-6 h-6 rounded-full flex-shrink-0 ${colorFilter === c ? 'ring-2 ring-offset-1 ring-slate-500 scale-110' : ''}`} />
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Ico d={IC.highlight} size={32} cls="text-slate-200 mb-3" />
            <p className="text-sm text-slate-400">No highlights yet</p>
            <p className="text-xs text-slate-300 mt-1">Select text while reading to highlight</p>
          </div>
        ) : filtered.map((h) => (
          <div key={h.id} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-start gap-2">
              <span className="w-3 h-3 rounded-full flex-shrink-0 mt-1" style={{ background: h.colorHex || '#FCD34D' }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-700 leading-relaxed italic">"{h.selectedText}"</p>
                {h.noteText && <p className="text-xs text-indigo-600 mt-1">{h.noteText}</p>}
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-slate-300">{h.createdAt ? new Date(h.createdAt).toLocaleDateString() : ''}</p>
                  {onDelete && <button onClick={() => onDelete(h.id)} className="text-slate-300 hover:text-red-400"><Ico d={IC.trash} size={12} /></button>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Notes Panel ──────────────────────────────────────────────────────────────
export function NotesPanel({ onClose, notes = [], onCreateNote, onDeleteNote }) {
  const [title, setTitle]   = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!content.trim() || !onCreateNote) return;
    setSaving(true);
    await onCreateNote({ title: title || 'Untitled', contentRaw: content });
    setTitle(''); setContent('');
    setSaving(false);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 flex-shrink-0">
        <Ico d={IC.note} size={16} cls="text-indigo-500" />
        <p className="text-sm font-bold text-slate-900 flex-1">My Notes</p>
        <span className="text-xs font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{notes.length}</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 ml-1"><Ico d={IC.x} size={16} /></button>
      </div>
      <div className="p-3 border-b border-slate-100 bg-slate-50 flex-shrink-0">
        <input className={`${INPUT_CLS} mb-2 text-xs`} placeholder="Note title…" value={title} onChange={(e) => setTitle(e.target.value)} />
        <textarea rows={3} className={`${INPUT_CLS} resize-none text-xs`} placeholder="Type your note…" value={content} onChange={(e) => setContent(e.target.value)} />
        <button onClick={save} disabled={saving || !content.trim()} className="mt-2 w-full py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 disabled:opacity-60 flex items-center justify-center gap-1.5">
          <Ico d={IC.plus} size={13} />{saving ? 'Saving…' : 'Save Note'}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
        {notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Ico d={IC.note} size={32} cls="text-slate-200 mb-3" />
            <p className="text-sm text-slate-400">No notes yet</p>
          </div>
        ) : notes.map((n) => (
          <div key={n.id} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="text-xs font-bold text-slate-800">{n.title || 'Untitled'}</p>
              {onDeleteNote && <button onClick={() => onDeleteNote(n.id)} className="text-slate-300 hover:text-red-500"><Ico d={IC.trash} size={13} /></button>}
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">{n.contentRaw || n.content}</p>
            <div className="flex flex-wrap gap-1 mt-2">
              {(n.tags || []).map((t) => <span key={t} className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-full">{t}</span>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Bookmarks Panel ──────────────────────────────────────────────────────────
export function BookmarksPanel({ onClose, bookmarks = [], onNavigate }) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 flex-shrink-0">
        <Ico d={IC.bookmark} size={16} cls="text-indigo-500" />
        <p className="text-sm font-bold text-slate-900 flex-1">Bookmarks</p>
        <span className="text-xs font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{bookmarks.length}</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 ml-1"><Ico d={IC.x} size={16} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
        {bookmarks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Ico d={IC.bookmark} size={32} cls="text-slate-200 mb-3" />
            <p className="text-sm text-slate-400">No bookmarks yet</p>
            <p className="text-xs text-slate-300 mt-1">Click the star icon to bookmark a topic</p>
          </div>
        ) : bookmarks.map((b) => (
          <div key={b.id} onClick={() => onNavigate && onNavigate(b)}
            className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3 hover:border-indigo-300 cursor-pointer">
            <Ico d={IC.star} size={16} cls="text-amber-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-slate-800 truncate">{b.topicTitle}</p>
              <p className="text-xs text-slate-400">{b.chapterTitle}</p>
            </div>
            <Ico d={IC.chevR} size={14} cls="text-slate-300 flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}